import type {IOwayoDataModel} from "../model";
import {EventListener, EventVerteiler} from "../../global/eventVerteiler";
import {ColorUtils} from "../colorUtils";
import {ChromoLogoColor} from "../selectableObjects";

import {PalettenLoader} from "../palettenLoader";
import {PATTERN_EVENTNAMES, PatternFactory} from "./patternFactory";
import type {StockLogo} from "../selectableObjects";
import globalsObject from "../globalsObject";
import {StocklogoController} from "../stocklogos/stocklogoController";
import type {StockLogoPoolEntry} from "../stocklogos/stocklogoController";
import {logIfDebug} from "../../global/logFunctions";


export class PatternInEntwurf {
    public readonly stockLogoID;
    public readonly forColorCode;
    public tileWidthInCM;
    public colors: ChromoLogoColor[];

    public constructor(stockLogoID, forColorCode, tileWidthInCM, colors: ChromoLogoColor[]) {
        this.stockLogoID = stockLogoID;
        this.forColorCode = forColorCode;
        this.tileWidthInCM = tileWidthInCM;

        this.colors = colors.map(c => ChromoLogoColor.clone(c));
    }

    public getColor(colorCode) {
        return this.colors.find(c=>c.colorCode === colorCode);
    }
}


export class PatternEntwurfsobjektInteractor extends EventListener {
    private model: IOwayoDataModel;
    private readonly getColorCodeFromColorCodeNr;

    constructor(model:IOwayoDataModel, getColorCodeFromColorCodeNr:(number)=>string) {
        super();
        this.model = model;
        this.getColorCodeFromColorCodeNr = getColorCodeFromColorCodeNr;
        this.initEventListeners();
    }

    public colorCodeHasPattern(colorCodeNr: number) {
        return this.model.entwurf.getPatterns().some(
            p=>p.forColorCode === this.getColorCodeFromColorCodeNr(colorCodeNr)
        );
    }

    private initEventListeners() {
        const $this = this;
        this.addEventListener(PATTERN_EVENTNAMES.CREATED, (data:{stockLogo:StockLogo})=>this.patternCreated(data.stockLogo))
            .addEventListener(PATTERN_EVENTNAMES.DELETED, (data)=>{
                this.patternDeleted(data.colorCodeNr, data.ignoreInHistory);
                EventVerteiler.broadcast("broadcastUsedColorsEvent");
            })
            .addEventListener(PATTERN_EVENTNAMES.COLOR_CHANGED, (data:{colorCodeOnEntwurf:string, colorCodeInPattern:string, newColorHex:string})=>
                this.patternColorChanged(data.colorCodeOnEntwurf, data.colorCodeInPattern, data.newColorHex))
            .addEventListener(PATTERN_EVENTNAMES.SIZE_CHANGED, (data:{colorCodeOnEntwurf:string, newSize:number, oldSize:number})=>
                this.patternSizeChanged(data.colorCodeOnEntwurf, data.newSize))
            .addEventListener(PATTERN_EVENTNAMES.LOADED, (data)=>{
                data.patterns.forEach(function(p:PatternInEntwurf){
                    PatternFactory.createPattern(p.stockLogoID, p.colors, globalsObject.controller.getColorCodeNr(p.forColorCode), parseFloat(p.tileWidthInCM));
                    EventVerteiler.broadcast("broadcastUsedColorsEvent");
                })
            })
            .addEventListener("patternColorVersionChangedEvent", function(data) {
                $this.colorversionChanged(data.colorcodeNr, data.colors);
            })
            .addEventListener(PATTERN_EVENTNAMES.SETPATTERN_TO_ALIAS, (data: { receiverColorCodeNr: number, donorColorCodeNr: number }) => {
                if (this.colorCodeHasPattern(data.receiverColorCodeNr)) {
                    EventVerteiler.broadcast(PATTERN_EVENTNAMES.DELETED, {colorCodeNr: data.receiverColorCodeNr});
                }
                if (this.colorCodeHasPattern(data.donorColorCodeNr)) {
                    const pattern = this.getPattern(this.model.entwurf.getColorCodeFromColorCodeNr(data.donorColorCodeNr));
                    const stockLogo = StocklogoController.getLogo(pattern.stockLogoID) as StockLogoPoolEntry;
                    PatternFactory.createPattern(stockLogo.stockLogoID, pattern.colors, data.receiverColorCodeNr, pattern.tileWidthInCM);
                }
            })


        ;
    }

    private colorversionChanged(colorcodeNr, colors) {
        const pattern = this.getPattern(globalsObject.controller.getColorCodeFromColorCodeNr(colorcodeNr));
        pattern.colors.forEach(patterncolor => {
            const colorIncoming = colors.find(c => c.colorCode === patterncolor.colorCode);
            if (colorIncoming == null) {
                return;
            }
            patterncolor.colorIntValue = colorIncoming.colorIntValue;
        })
    }

    private patternCreated(stockLogo: StockLogo) {
        this.patternDeleted(stockLogo.clampToColor);
        const colorCode = this.getColorCodeFromColorCodeNr(stockLogo.clampToColor);
        this.model.entwurf.addPattern(
            new PatternInEntwurf(
                stockLogo.stockLogoID,
                colorCode,
                stockLogo.targetSizeCM || stockLogo.standardSizeCM, stockLogo.colors)
        );
        const primaryColor = stockLogo.getPrimaryColor() as ChromoLogoColor;
        this.broadcastMainColorChange(colorCode, this.getClosestcolorInPaletteForColorCode(colorCode, ColorUtils.decToHex(primaryColor.colorIntValue)));
        EventVerteiler.broadcast("broadcastUsedColorsEvent");
    }

    private patternDeleted(colorCodeNr: number, ignoreInHistory=false) {
        const colorCode = this.getColorCodeFromColorCodeNr(colorCodeNr);
        this.model.entwurf.deletePatternForColorCode(colorCode, ignoreInHistory)
    }

    private getPattern(colorCodeOnEntwurf: string): PatternInEntwurf {
        return this.model.entwurf.getPattern(colorCodeOnEntwurf);
    }

    private broadcastMainColorChange = (colorCode: string, colorHexValue: string) => {
        globalsObject.controller.colorController.changeColor(colorCode, colorHexValue, "CM5 owayo Farbsystem", "no", true);
    }

    private patternColorChanged(colorCodeOnEntwurf, colorCodeInPattern, colorHexValue) {
        const patternInEntwurf = this.getPattern(colorCodeOnEntwurf);
        if (patternInEntwurf === null) {
            logIfDebug("pattern for " + colorCodeOnEntwurf + " not found!");
            return;
        }
        const color = patternInEntwurf.getColor(colorCodeInPattern) as ChromoLogoColor;
        color.colorIntValue = ColorUtils.hexToDec(colorHexValue);
        if (PatternEntwurfsobjektInteractor.ispatternColorWithGreatespercentage(color)) {
            this.broadcastMainColorChange(patternInEntwurf.forColorCode, this.getClosestcolorInPaletteForColorCode(patternInEntwurf.forColorCode, colorHexValue));
        }
        EventVerteiler.broadcast("broadcastUsedColorsEvent");
    }

    private getClosestcolorInPaletteForColorCode(colorCode, colorhexValueToMatch) {
        try {
            const color = this.model.entwurf.getColorObjects().filter(c=>c.colorCode === colorCode);
            if(color.length ===0) {
                return;
            }
            const cluster = PalettenLoader.getFlattenedPalettenCluster(color[0].palettenCluster);
            const matched = ColorUtils.getColorWithMinDeviationFromPalette(ColorUtils.hexToDec(colorhexValueToMatch), cluster);

            return ColorUtils.decToHex(matched);
        } catch (e) {
            return colorhexValueToMatch;
        }

    }

    private static ispatternColorWithGreatespercentage = (color: ChromoLogoColor) => color.farbnummerNachFarbanteil === 1;

    private patternSizeChanged(colorCodeOnEntwurf: string, size: number) {
        const patternInEntwurf = this.getPattern(colorCodeOnEntwurf);
        patternInEntwurf.tileWidthInCM = size;
    }

}
